import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, User, Package, Receipt, TrendingUp,
  ArrowLeft, LogOut, CreditCard, Tag, ChevronRight, Eye, X,
  Calendar, MapPin, Clock, Play, ArrowRight, QrCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, api, isAuthenticated } = useAuth();
  const { hasActiveSession, session, cartItems, total, itemCount } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [ordersRes, offersRes] = await Promise.all([
        api.get('/orders'),
        api.get('/offers')
      ]);
      
      setOrders(ordersRes.data || []);
      setOffers(offersRes.data?.filter(o => o.is_active) || []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  };

  // Calculate real analytics from orders
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    
    let totalSpent = 0;
    let monthSpent = 0;
    let totalItems = 0;
    let categoryCount = {};
    let productCount = {};
    
    orders.forEach(order => {
      const orderTotal = order.final_total || order.total || 0;
      totalSpent += orderTotal;
      
      const orderDate = new Date(order.created_at);
      if (orderDate.getMonth() === thisMonth && orderDate.getFullYear() === thisYear) {
        monthSpent += orderTotal;
      }
      
      // Count items and categories
      (order.items || []).forEach(item => {
        totalItems += item.quantity || 1;
        const cat = item.category || 'Other';
        categoryCount[cat] = (categoryCount[cat] || 0) + (item.quantity || 1);
        productCount[item.name] = (productCount[item.name] || 0) + (item.quantity || 1);
      });
    });
    
    // Find top category
    let topCategory = 'N/A';
    let maxCatCount = 0;
    Object.entries(categoryCount).forEach(([cat, count]) => {
      if (count > maxCatCount) {
        topCategory = cat;
        maxCatCount = count;
      }
    });
    
    // Find top product
    let topProduct = 'N/A';
    let maxProdCount = 0;
    Object.entries(productCount).forEach(([prod, count]) => {
      if (count > maxProdCount) {
        topProduct = prod;
        maxProdCount = count;
      }
    });
    
    // Calculate savings (from discounts)
    const totalSavings = orders.reduce((sum, o) => sum + (o.discount || 0), 0);
    
    return {
      totalOrders: orders.length,
      totalSpent,
      monthSpent,
      avgOrder: orders.length > 0 ? totalSpent / orders.length : 0,
      totalItems,
      avgItemsPerOrder: orders.length > 0 ? Math.round(totalItems / orders.length) : 0,
      topCategory,
      topProduct,
      totalSavings
    };
  }, [orders]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-slate-600" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-lg text-slate-800">My Dashboard</h1>
          </div>
          <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100" onClick={handleLogout}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Combined Welcome + Session Status Card */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          {hasActiveSession ? (
            // Active Session Card with User Info
            <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50 shadow-lg shadow-teal-100/50 overflow-hidden">
              <CardContent className="p-0">
                {/* User Welcome Strip */}
                <div className="px-4 sm:px-6 py-3 bg-white/50 border-b border-teal-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-md">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{user?.name || 'Welcome back!'}</p>
                      <p className="text-xs text-slate-500">{user?.email || user?.whatsapp_number}</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500 text-white text-xs animate-pulse">Shopping</Badge>
                </div>
                
                {/* Session Details */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/30">
                        <ShoppingCart className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-800">Active Shopping Session</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Package className="w-4 h-4" /> {itemCount} items
                          </span>
                          <span className="flex items-center gap-1 font-semibold text-teal-700">
                            <CreditCard className="w-4 h-4" /> ₹{total.toFixed(2)}
                          </span>
                          {session?.cart_code && (
                            <span className="text-xs text-slate-500">Cart: {session.cart_code}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/shop')} 
                      className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/30 h-11 sm:h-12 px-6"
                    >
                      <Play className="w-4 h-4 mr-2" /> Resume Shopping
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            // No Active Session - Combined Welcome + Start Shopping
            <Card className="border-slate-200 bg-white overflow-hidden">
              <CardContent className="p-0">
                {/* User Welcome Strip */}
                <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-md">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{user?.name || 'Welcome!'}</p>
                      <p className="text-xs text-slate-500">{user?.email || user?.whatsapp_number}</p>
                    </div>
                  </div>
                </div>
                
                {/* Start Shopping CTA */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-slate-100 flex items-center justify-center">
                        <QrCode className="w-6 h-6 sm:w-7 sm:h-7 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-800">Ready to Shop?</h3>
                        <p className="text-sm text-slate-500">Scan a cart QR code or enter cart code to start</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/')} 
                      className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white shadow-lg shadow-teal-500/25 h-11 sm:h-12 px-6"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" /> Start Shopping
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Stats Cards - Real data from orders */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalOrders}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Spent</p>
                  <p className="text-2xl font-bold text-slate-800">₹{stats.totalSpent.toFixed(0)}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Avg. Order</p>
                  <p className="text-2xl font-bold text-slate-800">₹{stats.avgOrder.toFixed(0)}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-100">
            <TabsTrigger value="orders" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">Orders</TabsTrigger>
            <TabsTrigger value="offers" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">Offers</TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-4">
            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <Receipt className="w-5 h-5 text-teal-600" /> Order History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No orders yet</p>
                    <Button className="mt-4 bg-teal-500 hover:bg-teal-600 text-white" onClick={() => navigate('/')}>Start Shopping</Button>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {orders.map((order) => (
                        <div 
                          key={order.id} 
                          className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => setSelectedOrder(order)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={order.payment_status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                {order.payment_status}
                              </Badge>
                              <span className="text-xs text-slate-500 font-mono">{order.invoice_number}</span>
                            </div>
                            <span className="font-bold text-teal-600">₹{(order.final_total || order.total)?.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-3 text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {formatDate(order.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" /> {order.item_count || order.items?.length || 0} items
                              </span>
                              {order.cart_code && (
                                <span className="flex items-center gap-1">
                                  <ShoppingCart className="w-3 h-3" /> {order.cart_code}
                                </span>
                              )}
                            </div>
                            <Eye className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="offers" className="mt-4">
            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <Tag className="w-5 h-5 text-orange-500" /> Available Offers ({offers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {offers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No active offers</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {offers.map((offer) => (
                      <div key={offer.id} className="p-4 border border-slate-200 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{offer.title}</h4>
                            <p className="text-sm text-slate-500">{offer.description}</p>
                          </div>
                          <Badge className="bg-orange-500 text-white">
                            {offer.discount_type === 'percentage' 
                              ? `${offer.discount_value}% OFF` 
                              : `₹${offer.discount_value} OFF`}
                          </Badge>
                        </div>
                        {offer.min_purchase > 0 && (
                          <p className="text-xs text-slate-400 mt-2">
                            Min. purchase: ₹{offer.min_purchase}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            <Card className="border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <TrendingUp className="w-5 h-5 text-teal-600" /> Shopping Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Spending Summary - Real data */}
                  <div>
                    <h4 className="font-medium text-slate-800 mb-3">Spending Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-500">This Month</p>
                        <p className="text-xl font-bold text-slate-800">₹{stats.monthSpent.toFixed(0)}</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-xl">
                        <p className="text-sm text-slate-500">Total Savings</p>
                        <p className="text-xl font-bold text-emerald-600">₹{stats.totalSavings.toFixed(0)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Shopping Patterns - Real data */}
                  <div>
                    <h4 className="font-medium text-slate-800 mb-3">Shopping Patterns</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Top Category</span>
                        <Badge variant="outline" className="border-teal-300 text-teal-700">{stats.topCategory}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Most Purchased</span>
                        <Badge variant="outline" className="border-teal-300 text-teal-700 max-w-[150px] truncate">{stats.topProduct}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Total Items Purchased</span>
                        <Badge variant="outline" className="border-teal-300 text-teal-700">{stats.totalItems}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600">Avg. Items per Order</span>
                        <Badge variant="outline" className="border-teal-300 text-teal-700">{stats.avgItemsPerOrder}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Receipt className="w-5 h-5 text-teal-600" /> Invoice Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Invoice Header */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-slate-600">{selectedOrder.invoice_number}</span>
                  <Badge className={selectedOrder.payment_status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                    {selectedOrder.payment_status}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatDateTime(selectedOrder.created_at)}
                  </span>
                  {selectedOrder.cart_code && (
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="w-3 h-3" /> Cart: {selectedOrder.cart_code}
                    </span>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Items ({selectedOrder.items?.length || 0})</h4>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-800 truncate">{item.name}</p>
                          <p className="text-xs text-slate-500">₹{item.price} × {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-sm text-slate-700">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Applied Offers */}
              {selectedOrder.applied_offers?.length > 0 && (
                <div className="p-3 bg-teal-50 rounded-lg">
                  <p className="text-xs font-medium text-teal-700 mb-1">Applied Offers</p>
                  {selectedOrder.applied_offers.map((offer, idx) => (
                    <p key={idx} className="text-xs text-teal-600">{offer.title} - Saved ₹{offer.discount?.toFixed(2)}</p>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-700">₹{selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax</span>
                  <span className="text-slate-700">₹{selectedOrder.tax?.toFixed(2)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-teal-600">
                    <span>Discount</span>
                    <span>-₹{selectedOrder.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-200">
                  <span className="text-slate-800">Total Paid</span>
                  <span className="text-teal-600">₹{(selectedOrder.final_total || selectedOrder.total)?.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="text-xs text-slate-400">
                <p>Payment Method: {selectedOrder.payment_method || 'Card'}</p>
                {selectedOrder.payment_id && <p>Payment ID: {selectedOrder.payment_id}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
